import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  FileText, Edit3, Eye, Save, Send, Clock, History, X,
  ChevronDown, ChevronRight, CheckCircle, ArrowLeft, Globe,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const PAGE_TYPES = [
  { type: 'privacy_policy', label: 'Privacy Policy', slug: 'privacy-policy', icon: '🔒' },
  { type: 'terms_of_service', label: 'Terms of Service', slug: 'terms-of-service', icon: '📋' },
  { type: 'security', label: 'Security', slug: 'security', icon: '🛡️' },
  { type: 'contact', label: 'Contact', slug: 'contact', icon: '💬' },
];

const DEFAULT_CONTENT = {
  privacy_policy: `<h2>Introduction</h2><p>LedgerPro Accounting System ("LedgerPro", "we", "our", or "us") respects your privacy and is committed to protecting your personal and business information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our accounting and business management platform.</p><h2>Information We Collect</h2><p>We may collect the following types of information:</p><ul><li><strong>Account Information:</strong> Name, email address, phone number, and company/business details</li><li><strong>Billing Information:</strong> Payment details necessary to process subscriptions</li><li><strong>Accounting Data:</strong> Financial records, invoices, transactions, and business documents you enter into LedgerPro</li><li><strong>Technical Information:</strong> IP address, browser type, device information, and usage data</li></ul><h2>How We Use Information</h2><p>Your information is used to:</p><ul><li>Provide and maintain the LedgerPro service</li><li>Process transactions and generate financial documents</li><li>Provide customer support</li><li>Improve and enhance the platform</li><li>Ensure security and detect fraud</li><li>Comply with legal obligations</li></ul><h2>Data Storage &amp; Security</h2><p>We implement reasonable technical and organizational measures to protect your data, including encrypted data transmission, secure authentication, and role-based access controls.</p><h2>Contact</h2><p>For privacy-related inquiries, please contact us at {{support_email}}.</p>`,
  terms_of_service: `<h2>Acceptance of Terms</h2><p>By accessing or using LedgerPro Accounting System, you agree to be bound by these Terms of Service. If you do not agree, please do not use the service.</p><h2>Description of Service</h2><p>LedgerPro is an accounting and business management platform providing features including invoicing, estimates, sales orders, purchase orders, bills, customer/vendor management, inventory tracking, payments, financial reports, and business analytics.</p><h2>Account Registration</h2><ul><li>You must provide accurate and complete registration information</li><li>You are responsible for maintaining the confidentiality of your credentials</li><li>You are responsible for all activity under your account</li><li>You must notify us immediately of any unauthorized access</li></ul><h2>Acceptable Use</h2><p>You agree not to: abuse the platform, attempt unauthorized access, introduce malware, disrupt services, or use the system for unlawful purposes.</p><h2>Customer Data</h2><p>You retain ownership of all business and accounting data you enter into LedgerPro. We will not sell or share your data with third parties for advertising purposes.</p><h2>Disclaimer</h2><p>LedgerPro is a software tool. You remain solely responsible for the accuracy of your accounting, tax, financial, and business decisions.</p><h2>Contact</h2><p>For questions about these terms, contact {{support_email}}.</p>`,
  security: `<h2>Security at LedgerPro</h2><p>Protecting your business data is one of our highest priorities.</p><h2>Data Protection</h2><p>LedgerPro implements industry-standard security measures to protect your business and accounting information from unauthorized access, disclosure, and misuse.</p><h2>Encryption</h2><p>All data transmitted between your browser and LedgerPro servers is encrypted using TLS. Sensitive data at rest is encrypted using appropriate encryption methods.</p><h2>Authentication</h2><ul><li>Secure password-based authentication</li><li>Passwords are hashed and salted using bcrypt</li><li>Session management with automatic expiration</li><li>JWT-based token authentication</li></ul><h2>Access Control</h2><p>LedgerPro uses role-based access control (RBAC) with granular permissions:</p><ul><li><strong>Super Admin:</strong> Full platform access</li><li><strong>Administrator:</strong> Full company access</li><li><strong>Accountant:</strong> Financial data access</li><li><strong>User:</strong> Limited operational access</li><li><strong>Viewer:</strong> Read-only access</li></ul><h2>Backups &amp; Recovery</h2><p>Regular automated backups are performed to ensure data recovery capabilities.</p><h2>Incident Response</h2><p>Security incidents are investigated promptly and affected parties are notified as appropriate.</p><h2>Responsible Disclosure</h2><p>If you discover a security vulnerability, please report it to {{security_email}}.</p><h2>Contact</h2><p>For security inquiries, contact {{security_email}}.</p>`,
};

// Simple rich text toolbar
function RichTextEditor({ value, onChange }) {
  const exec = (cmd, val = null) => document.execCommand(cmd, false, val);

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, padding: '6px 8px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        {[
          { label: 'B', cmd: 'bold', style: { fontWeight: 800 } },
          { label: 'I', cmd: 'italic', style: { fontStyle: 'italic' } },
          { label: 'U', cmd: 'underline', style: { textDecoration: 'underline' } },
        ].map(b => (
          <button key={b.cmd} type="button" onClick={() => exec(b.cmd)} style={{ width: 28, height: 28, border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 12, ...b.style }}>{b.label}</button>
        ))}
        <div style={{ width: 1, background: '#e2e8f0', margin: '2px 4px' }} />
        {[1, 2, 3, 4].map(h => (
          <button key={h} type="button" onClick={() => exec('formatBlock', `h${h}`)} style={{ height: 28, padding: '0 8px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>H{h}</button>
        ))}
        <div style={{ width: 1, background: '#e2e8f0', margin: '2px 4px' }} />
        <button type="button" onClick={() => exec('insertUnorderedList')} style={{ height: 28, padding: '0 8px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 11 }}>• List</button>
        <button type="button" onClick={() => exec('insertOrderedList')} style={{ height: 28, padding: '0 8px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 11 }}>1. List</button>
        <button type="button" onClick={() => exec('formatBlock', 'blockquote')} style={{ height: 28, padding: '0 8px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 11 }}>" Quote</button>
        <button type="button" onClick={() => exec('insertHorizontalRule')} style={{ height: 28, padding: '0 8px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 11 }}>— Line</button>
        <button type="button" onClick={() => { const url = prompt('Enter URL:'); if (url) exec('createLink', url); }} style={{ height: 28, padding: '0 8px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 11 }}>🔗 Link</button>
      </div>
      <div
        contentEditable
        suppressContentEditableWarning
        onInput={e => onChange(e.target.innerHTML)}
        dangerouslySetInnerHTML={{ __html: value }}
        style={{ minHeight: 400, padding: 16, fontSize: 14, lineHeight: 1.7, outline: 'none' }}
      />
    </div>
  );
}

export default function SuperAdminLegal() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('ledgerpro_token');

  const load = useCallback(async () => {
    try {
      const res = await fetch(API_BASE + '/legal', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setPages(Array.isArray(data) ? data : []);
    } catch { /* */ }
  }, [token]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  const handleSave = async (publish = false) => {
    if (!editing) return;
    setSaving(true);
    try {
      // Save content
      const res = await fetch(API_BASE + '/legal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(editing),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      if (publish) {
        const pubRes = await fetch(API_BASE + `/legal/${data.id}/publish`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!pubRes.ok) throw new Error('Failed to publish');
        toast.success('Published!');
      } else {
        toast.success('Draft saved');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublish = async (id) => {
    if (!window.confirm('Unpublish this page? It will no longer be visible to users.')) return;
    try {
      await fetch(API_BASE + `/legal/${id}/unpublish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Unpublished');
      load();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const loadVersions = async (id) => {
    try {
      const res = await fetch(API_BASE + `/legal/${id}/versions`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setVersions(Array.isArray(data) ? data : []);
      setShowVersions(true);
    } catch { toast.error('Failed to load versions'); }
  };

  const handleRestore = async (contentId, versionId) => {
    if (!window.confirm('Restore this version? The current content will be saved as a version.')) return;
    try {
      await fetch(API_BASE + `/legal/${contentId}/restore/${versionId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Version restored');
      setShowVersions(false);
      load();
    } catch (err) {
      toast.error('Failed');
    }
  };

  // Start editing a page type
  const startEdit = (pageType) => {
    const existing = pages.find(p => p.type === pageType.type);
    setEditing({
      type: pageType.type,
      title: existing?.title || pageType.label,
      slug: existing?.slug || pageType.slug,
      description: existing?.description || '',
      content: existing?.content || DEFAULT_CONTENT[pageType.type] || '',
      seo_title: existing?.seo_title || '',
      seo_description: existing?.seo_description || '',
      seo_keywords: existing?.seo_keywords || '',
    });
    setSelected(null);
  };

  if (loading) return <div className="spinner" style={{ margin: '80px auto' }} />;

  // If editing, show editor
  if (editing) {
    return (
      <div>
        <div className="sa-toolbar">
          <button className="sa-btn sa-btn-ghost" onClick={() => setEditing(null)}>
            <ArrowLeft size={14} /> Back
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="sa-btn sa-btn-outline" onClick={() => handleSave(false)} disabled={saving}>
              <Save size={14} /> {saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button className="sa-btn sa-btn-primary" onClick={() => handleSave(true)} disabled={saving}>
              <Send size={14} /> Publish
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20 }}>
          {/* Main editor */}
          <div>
            <div className="sa-card" style={{ marginBottom: 16 }}>
              <div className="sa-card-body">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div className="sa-form-group">
                    <label className="sa-form-label">Page Title *</label>
                    <input className="sa-input" value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})} />
                  </div>
                  <div className="sa-form-group">
                    <label className="sa-form-label">URL Slug</label>
                    <input className="sa-input" value={editing.slug} onChange={e => setEditing({...editing, slug: e.target.value})} />
                  </div>
                </div>
                <div className="sa-form-group">
                  <label className="sa-form-label">Short Description</label>
                  <input className="sa-input" value={editing.description} onChange={e => setEditing({...editing, description: e.target.value})} placeholder="Brief description of this page" />
                </div>
              </div>
            </div>

            <div className="sa-card" style={{ marginBottom: 16 }}>
              <div className="sa-card-header">
                <h3 className="sa-card-title">Page Content</h3>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                  Use {'{{company_name}}'}, {'{{support_email}}'}, {'{{security_email}}'}, {'{{company_address}}'}, {'{{phone}}'}, {'{{website}}'} for dynamic values
                </span>
              </div>
              <div className="sa-card-body">
                <RichTextEditor value={editing.content} onChange={content => setEditing({...editing, content})} />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div>
            <div className="sa-card" style={{ marginBottom: 16 }}>
              <div className="sa-card-header">
                <h3 className="sa-card-title">SEO Settings</h3>
              </div>
              <div className="sa-card-body">
                <div className="sa-form-group">
                  <label className="sa-form-label">SEO Title</label>
                  <input className="sa-input" value={editing.seo_title} onChange={e => setEditing({...editing, seo_title: e.target.value})} placeholder="Page title for search engines" />
                </div>
                <div className="sa-form-group">
                  <label className="sa-form-label">SEO Description</label>
                  <textarea className="sa-input" rows={3} value={editing.seo_description} onChange={e => setEditing({...editing, seo_description: e.target.value})} placeholder="Meta description for search engines" />
                </div>
                <div className="sa-form-group">
                  <label className="sa-form-label">SEO Keywords</label>
                  <input className="sa-input" value={editing.seo_keywords} onChange={e => setEditing({...editing, seo_keywords: e.target.value})} placeholder="Comma-separated keywords" />
                </div>
              </div>
            </div>

            <div className="sa-card">
              <div className="sa-card-header">
                <h3 className="sa-card-title">Page Info</h3>
              </div>
              <div className="sa-card-body">
                <div style={{ fontSize: 12, color: '#64748b', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div><strong>Type:</strong> {PAGE_TYPES.find(p => p.type === editing.type)?.label}</div>
                  <div><strong>URL:</strong> /{editing.slug}</div>
                  <div><strong>Dynamic Vars:</strong></div>
                  <div style={{ fontFamily: 'monospace', fontSize: 11, background: '#f1f5f9', padding: 8, borderRadius: 4 }}>
                    {'{{company_name}}'}<br />
                    {'{{support_email}}'}<br />
                    {'{{security_email}}'}<br />
                    {'{{company_address}}'}<br />
                    {'{{phone}}'}<br />
                    {'{{website}}'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Version history modal
  if (showVersions) {
    return (
      <div>
        <div className="sa-toolbar">
          <button className="sa-btn sa-btn-ghost" onClick={() => setShowVersions(false)}>
            <ArrowLeft size={14} /> Back
          </button>
        </div>
        <div className="sa-card">
          <div className="sa-card-header">
            <h3 className="sa-card-title"><History size={14} /> Version History</h3>
          </div>
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Title</th>
                  <th>Changed By</th>
                  <th>Date</th>
                  <th>Summary</th>
                  <th className="sa-text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {versions.length === 0 ? (
                  <tr><td colSpan={6} className="sa-empty-row">No version history</td></tr>
                ) : versions.map(v => (
                  <tr key={v.id}>
                    <td><span className="sa-badge-status sa-badge-active">v{v.version}</span></td>
                    <td style={{ fontSize: 13, fontWeight: 600 }}>{v.title}</td>
                    <td style={{ fontSize: 12 }}>{v.changed_by_name || '—'}</td>
                    <td style={{ fontSize: 12 }}>{new Date(v.changed_at).toLocaleString()}</td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{v.change_summary || '—'}</td>
                    <td className="sa-text-right">
                      <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => handleRestore(v.legal_content_id, v.id)}>
                        Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Page list view
  return (
    <div>
      <div className="sa-toolbar">
        <span className="sa-card-title">Legal & Compliance Content Management</span>
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        {PAGE_TYPES.map(pt => {
          const page = pages.find(p => p.type === pt.type);
          return (
            <div key={pt.type} className="sa-card" style={{ cursor: 'pointer' }} onClick={() => setSelected(selected === pt.type ? null : pt.type)}>
              <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 24 }}>{pt.icon}</span>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{pt.label}</h3>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748b' }}>/{pt.slug}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {page ? (
                    <span className={`sa-badge-status ${page.status === 'published' ? 'sa-badge-active' : 'sa-badge-pending'}`}>
                      {page.status === 'published' ? 'Published' : 'Draft'} · v{page.version}
                    </span>
                  ) : (
                    <span className="sa-badge-status sa-badge-expired">Not Created</span>
                  )}
                  {selected === pt.type ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>
              </div>

              {selected === pt.type && (
                <div style={{ padding: '0 20px 16px', borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                  {page && (
                    <div style={{ marginBottom: 12, fontSize: 12, color: '#64748b' }}>
                      {page.title && <div><strong>Title:</strong> {page.title}</div>}
                      {page.published_at && <div><strong>Published:</strong> {new Date(page.published_at).toLocaleString()}</div>}
                      {page.author_name && <div><strong>Last updated by:</strong> {page.author_name}</div>}
                      {page.description && <div style={{ marginTop: 4 }}>{page.description}</div>}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="sa-btn sa-btn-primary sa-btn-sm" onClick={(e) => { e.stopPropagation(); startEdit(pt); }}>
                      <Edit3 size={12} /> {page ? 'Edit' : 'Create'}
                    </button>
                    {page && page.status === 'published' && (
                      <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={(e) => { e.stopPropagation(); handleUnpublish(page.id); }}>
                        Unpublish
                      </button>
                    )}
                    {page && (
                      <button className="sa-btn sa-btn-ghost sa-btn-sm" onClick={(e) => { e.stopPropagation(); loadVersions(page.id); }}>
                        <History size={12} /> Versions
                      </button>
                    )}
                    {page && (
                      <a href={`/${page.slug}`} target="_blank" rel="noreferrer" className="sa-btn sa-btn-ghost sa-btn-sm" onClick={e => e.stopPropagation()}>
                        <Globe size={12} /> View Live
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
